import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Coins, Layers, Package } from 'lucide-react';
import { stockService } from '../../services/api';
import { formatCurrency } from '../../utils/formatters';

const num = (s: string | number | null | undefined) => Number(s ?? 0);

const StockValorisation: React.FC = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['stock', 'valorisation'],
    queryFn: () => stockService.getValorisation().then((r) => r.data),
  });

  if (isLoading || !data) {
    return <div className="p-6 text-gray-500">Calcul de la valorisation…</div>;
  }

  const intrants = data.intrants || [];
  const categories = data.par_categorie || [];

  return (
    <div className="w-full space-y-6">
      {/* Synthèse */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="relative overflow-hidden rounded-2xl p-5 text-white sm:col-span-1" style={{ background: 'var(--grad-teal)', boxShadow: 'var(--shadow-btn-teal)' }}>
          <Coins className="h-7 w-7 mb-2 opacity-80" />
          <p className="text-sm opacity-80">Valeur totale du stock (coût d'achat)</p>
          <p className="text-2xl font-bold mt-1">{formatCurrency(num(data.valeur_totale))}</p>
        </div>
        <div className="rounded-2xl p-5 bg-white shadow-sm flex items-center gap-3">
          <span className="p-2.5 rounded-xl bg-[color:var(--blue-soft)] text-[#1565B8]"><Package className="h-5 w-5" /></span>
          <div>
            <p className="text-sm text-gray-500">Produits valorisés</p>
            <p className="text-xl font-bold text-gray-900">{intrants.length}</p>
          </div>
        </div>
        <div className="rounded-2xl p-5 bg-white shadow-sm flex items-center gap-3">
          <span className="p-2.5 rounded-xl bg-[color:var(--blue-soft)] text-[#1565B8]"><Layers className="h-5 w-5" /></span>
          <div>
            <p className="text-sm text-gray-500">Catégories</p>
            <p className="text-xl font-bold text-gray-900">{categories.length}</p>
          </div>
        </div>
      </div>

      {/* Répartition par catégorie */}
      {categories.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Répartition par catégorie</h3>
          <div className="space-y-2">
            {categories.map((c) => {
              const total = num(data.valeur_totale) || 1;
              const pct = (num(c.valeur) / total) * 100;
              return (
                <div key={c.categorie}>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">{c.categorie}</span>
                    <span className="font-medium text-gray-900">{formatCurrency(num(c.valeur))}</span>
                  </div>
                  <div className="bg-gray-100 rounded-full h-2 mt-1 overflow-hidden">
                    <div className="bg-[color:var(--teal)] h-2 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Détail par intrant */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Détail par produit</h3>
          <p className="text-xs text-gray-400">Valeur = stock actuel × coût moyen pondéré (CMP) d'achat</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                <th className="px-4 py-2">Code</th>
                <th className="px-4 py-2">Produit</th>
                <th className="px-4 py-2">Catégorie</th>
                <th className="px-4 py-2 text-right">Stock</th>
                <th className="px-4 py-2 text-right">CMP</th>
                <th className="px-4 py-2 text-right">Valeur</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {intrants.map((i) => (
                <tr key={i.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm text-gray-500">{i.code}</td>
                  <td className="px-4 py-2 text-sm text-gray-800">{i.nom}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{i.categorie || '—'}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-700">{i.stock_actuel} {i.unite}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-700">{formatCurrency(num(i.cout_moyen_pondere))}</td>
                  <td className="px-4 py-2 text-sm text-right font-semibold text-gray-900">{formatCurrency(num(i.valeur_stock))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StockValorisation;
